/**
 * LockedCard — renders gated feature UI.
 *
 * Spec rule: never HIDE a paid feature. Render it locked with a clear
 * "Upgrade to {Tier}" CTA. Discoverability is part of the upgrade loop.
 */
import { Link } from '@tanstack/react-router';
import { LockIcon, SparklesIcon } from 'lucide-react';
import { type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export function LockedCard({
  title,
  description,
  requiredTier,
  children,
}: {
  title: string;
  description: string;
  requiredTier: 'starter' | 'growth' | 'scale';
  children?: ReactNode;
}) {
  const tierLabel = requiredTier[0]!.toUpperCase() + requiredTier.slice(1);
  return (
    <Card className="relative overflow-hidden bg-slate-50 dark:bg-slate-950/50">
      <div className="absolute right-4 top-4 flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
        <LockIcon className="h-3 w-3" />
        Locked
      </div>
      <CardHeader>
        <CardTitle className="text-slate-600 dark:text-slate-400">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      {children && <div className="opacity-60">{children}</div>}
      <div className="mt-4">
        <Button asChild size="sm">
          <Link to="/app/billing">
            <SparklesIcon className="h-3.5 w-3.5" /> Upgrade to {tierLabel}
          </Link>
        </Button>
      </div>
    </Card>
  );
}

export function UnlockedFeatureCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      {children}
    </Card>
  );
}
